import React, { useEffect, useRef, useState } from 'react'

const ERROR_IMG_SRC =
  'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iODgiIGhlaWdodD0iODgiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyIgc3Ryb2tlPSIjMDAwIiBzdHJva2UtbGluZWpvaW49InJvdW5kIiBvcGFjaXR5PSIuMyIgZmlsbD0ibm9uZSIgc3Ryb2tlLXdpZHRoPSIzLjciPjxyZWN0IHg9IjE2IiB5PSIxNiIgd2lkdGg9IjU2IiBoZWlnaHQ9IjU2IiByeD0iNiIvPjxwYXRoIGQ9Im0xNiA1OCAxNi0xOCAzMiAzMiIvPjxjaXJjbGUgY3g9IjUzIiBjeT0iMzUiIHI9IjciLz48L3N2Zz4KCg=='

type PictureSource = {
  type?: string
  srcSet: string
  sizes?: string
}

type ImageWithFallbackProps = React.ImgHTMLAttributes<HTMLImageElement> & {
  wrapperClassName?: string
  transitionEffect?: 'fade' | 'blur' | 'none'
  placeholderSrc?: string
  pictureSources?: PictureSource[]
  critical?: boolean
}

const TARGET_WIDTHS = [320, 480, 640, 800, 960, 1200, 1600]
const DEFAULT_RESPONSIVE_SIZES = '(min-width: 1280px) 35vw, (min-width: 768px) 55vw, 60vw'

const sanitizeUrlForWidth = (fullSrc: string, width: number) => {
  const suffixMatch = fullSrc.match(/_w(\d+)(\.[^?]+)(\?.*)?$/i)
  if (suffixMatch) {
    const currentWidth = Number(suffixMatch[1])
    if (!Number.isFinite(currentWidth) || width > currentWidth) return undefined
    return fullSrc.replace(/_w\d+/i, `_w${width}`)
  }
  try {
    const url = new URL(fullSrc)
    const existingWidth = Number(url.searchParams.get('w') || url.searchParams.get('width') || '')
    if (!Number.isFinite(existingWidth) || width > existingWidth) return undefined
    url.searchParams.set('w', String(width))
    url.searchParams.set('width', String(width))
    return url.toString()
  } catch (_error) {
    // Ignore relative URLs since we cannot safely derive width variants
  }
  return undefined
}

const toWebpUrl = (url: string) => {
  if (/\.webp(\?|$)/i.test(url)) return null
  if (!/\.[a-z0-9]+(\?|$)/i.test(url)) return null
  return url.replace(/(\.[^./?]+)(\?.*)?$/i, '.webp$2')
}

const buildWebpSrcSet = (srcSet?: string) => {
  if (!srcSet) return undefined
  const converted = srcSet
    .split(',')
    .map((entry) => entry.trim())
    .filter(Boolean)
    .map((entry) => {
      const match = entry.match(/^(\S+)(\s.+)$/)
      if (!match) return null
      const webpUrl = toWebpUrl(match[1])
      if (!webpUrl) return null
      return `${webpUrl}${match[2]}`
    })
    .filter((value): value is string => Boolean(value))
  return converted.length ? converted.join(', ') : undefined
}

const buildResponsiveSrcSet = (fullSrc?: string) => {
  if (!fullSrc) return undefined
  const match = fullSrc.match(/_w(\d+)/i)
  let originalWidth = match ? Number(match[1]) : undefined
  if (!originalWidth) {
    try {
      const url = new URL(fullSrc)
      const param = Number(url.searchParams.get('w') || url.searchParams.get('width') || '')
      if (Number.isFinite(param)) {
        originalWidth = param
      }
    } catch (_error) {
      /* ignore */
    }
  }
  if (!originalWidth) return undefined
  const widths = TARGET_WIDTHS.filter((size) => size < originalWidth)
  widths.push(originalWidth)
  const variants = widths
    .map((size) => {
      const url = sanitizeUrlForWidth(fullSrc, size)
      return url ? { url, descriptor: `${size}w` } : null
    })
    .filter((value): value is { url: string; descriptor: string } => Boolean(value))
  if (!variants.length) return undefined
  return {
    srcSet: variants.map(({ url, descriptor }) => `${url} ${descriptor}`).join(', '),
    sizes: DEFAULT_RESPONSIVE_SIZES,
    variants,
  }
}

export function ImageWithFallback({
  wrapperClassName,
  className,
  style,
  src,
  alt,
  onLoad,
  transitionEffect = 'fade',
  placeholderSrc,
  pictureSources,
  critical = false,
  ...rest
}: ImageWithFallbackProps) {
  const {
    loading: providedLoading,
    decoding: providedDecoding,
    srcSet: providedSrcSet,
    sizes: providedSizes,
    ...imgAttributes
  } = rest
  const [didError, setDidError] = useState(false)
  const [shouldShowImage, setShouldShowImage] = useState(() => (critical ? true : false))
  const imgRef = useRef<HTMLImageElement | null>(null)
  const autoResponsive = !providedSrcSet && src ? buildResponsiveSrcSet(src) : undefined
  const responsiveSrcSet = providedSrcSet ?? autoResponsive?.srcSet
  const responsiveSizes = providedSizes ?? autoResponsive?.sizes
  const computedPictureSources =
    pictureSources ||
    (() => {
      const srcSetString = responsiveSrcSet ?? autoResponsive?.srcSet
      if (!srcSetString) return undefined
      const candidateSizes = responsiveSizes ?? autoResponsive?.sizes ?? DEFAULT_RESPONSIVE_SIZES
      const webpSrcSet = buildWebpSrcSet(srcSetString)
      if (!webpSrcSet) return undefined
      return [{ type: 'image/webp', srcSet: webpSrcSet, sizes: candidateSizes }]
    })()
  const resolvedLoading = providedLoading ?? 'lazy'
  const resolvedDecoding = providedDecoding ?? 'async'
  const resolvedSrc = didError ? ERROR_IMG_SRC : src ?? placeholderSrc ?? ''

  const revealImage = () => {
    if (critical) return
    requestAnimationFrame(() => {
      requestAnimationFrame(() => setShouldShowImage(true))
    })
  }

  useEffect(() => {
    setDidError(false)
    setShouldShowImage(critical ? true : false)
  }, [src, placeholderSrc, critical])

  const handleLoad = (event: React.SyntheticEvent<HTMLImageElement, Event>) => {
    revealImage()
    onLoad?.(event)
  }

  const handleError = () => {
    setDidError(true)
    setShouldShowImage(true)
  }

  useEffect(() => {
    if (critical) return
    const img = imgRef.current
    if (img && img.complete && img.naturalWidth > 0 && img.naturalHeight > 0) {
      revealImage()
    }
  }, [resolvedSrc, critical])

  const resolvedAlt = didError ? 'Image unavailable' : alt
  const effectClasses =
    transitionEffect === 'blur'
      ? shouldShowImage
        ? 'opacity-100 blur-0 scale-100'
        : 'opacity-80 blur-sm scale-[1.02]'
      : transitionEffect === 'fade'
        ? shouldShowImage
          ? 'opacity-100'
          : 'opacity-0'
        : 'opacity-100'
  const transitionClasses =
    transitionEffect === 'blur'
      ? 'transition-[opacity,transform,filter] duration-500 ease-out'
      : transitionEffect === 'fade'
        ? 'transition-opacity duration-500 ease-out'
        : ''

  const imageElement = (
    <img
      {...imgAttributes}
      ref={imgRef}
      src={resolvedSrc}
      alt={resolvedAlt}
      style={style}
      data-original-url={src}
      data-loaded={shouldShowImage ? 'true' : 'false'}
      loading={resolvedLoading}
      decoding={resolvedDecoding}
      srcSet={responsiveSrcSet}
      sizes={responsiveSizes}
      className={`block h-full w-full object-cover ${transitionClasses} ${effectClasses} ${className ?? ''}`}
      onLoad={handleLoad}
      onError={handleError}
    />
  )

  return (
    <span
      className={`relative block h-full w-full overflow-hidden ${wrapperClassName ?? ''}`}
      aria-live="polite"
    >
      {!critical && (
        <span
          aria-hidden="true"
          className={`pointer-events-none absolute inset-0 bg-muted transition-opacity duration-500 ${
            shouldShowImage ? 'opacity-0' : 'opacity-100 animate-pulse'
          }`}
        />
      )}
      {computedPictureSources ? (
        <picture className="block h-full w-full">
          {computedPictureSources.map((source, index) => (
            <source key={`${source.srcSet}-${index}`} type={source.type} srcSet={source.srcSet} sizes={source.sizes} />
          ))}
          {imageElement}
        </picture>
      ) : (
        imageElement
      )}
    </span>
  )
}
